import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs';
import { ScfHoldPointDashboardCell, ScfHoldPointDashboardRow, ScfHoldPointDashboardSignature } from './scf-hold-point-dashboard.models';
import { ScfHoldPointDashboardService } from './scf-hold-point-dashboard.service';

@Component({
  selector: 'app-scf-hold-point-dashboard',
  templateUrl: './scf-hold-point-dashboard.component.html',
  styleUrls: ['./scf-hold-point-dashboard.component.scss']
})
export class ScfHoldPointDashboardComponent implements OnInit, OnDestroy {
  readonly filterControl = new FormControl('', { nonNullable: true });
  readonly destroy$ = new Subject<void>();

  rows: ScfHoldPointDashboardRow[] = [];
  filteredRows: ScfHoldPointDashboardRow[] = [];
  readonly visibleAmendmentRows = new Set<number>();
  loading = true;
  error = false;

  constructor(
    private readonly service: ScfHoldPointDashboardService,
    private readonly router: Router
  ) { }

  /** Loads the dashboard matrix and connects the requested free-text filter. */
  ngOnInit(): void {
    this.service.getDashboard()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: rows => {
          // Keep rows without hold points out if an older API payload includes them.
          this.rows = (rows ?? []).filter(row =>
            (row.holdPoints?.length ?? 0) > 0 || (row.amendmentHoldPoints?.length ?? 0) > 0
          );
          this.visibleAmendmentRows.clear();
          this.applyFilter(this.filterControl.value);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.error = true;
        }
      });

    this.filterControl.valueChanges
      .pipe(
        startWith(this.filterControl.value),
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.applyFilter(value));
  }

  /** Releases the filter subscription when the dashboard is removed. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Opens the existing SCF form route for the selected serial number.
   * @param row Dashboard row whose SCF should be opened.
   */
  openScf(row: ScfHoldPointDashboardRow): void {
    this.router.navigate(['/redirect', row.serialNo]);
  }

  /**
   * Shows or hides the pending amendment hold-point row for an SCF.
   * @param row Dashboard row whose amendment hold points are being toggled.
   * @param visible Whether the amendment hold points should be displayed.
   */
  toggleAmendmentHoldPoints(row: ScfHoldPointDashboardRow, visible: boolean): void {
    if (visible) {
      this.visibleAmendmentRows.add(row.id);
      return;
    }

    this.visibleAmendmentRows.delete(row.id);
  }

  /**
   * Determines whether pending amendment hold points are expanded for an SCF.
   * @param row Dashboard row being checked.
   * @returns True when the amendment row is visible.
   */
  isAmendmentHoldPointsVisible(row: ScfHoldPointDashboardRow): boolean {
    return this.visibleAmendmentRows.has(row.id);
  }

  /**
   * Returns only signature slots that contribute to the hold-point count.
   * @param cell Hold-point status cell.
   * @returns Required and enabled signatures for the cell.
   */
  getRequiredSignatures(cell: ScfHoldPointDashboardCell): ScfHoldPointDashboardSignature[] {
    return cell.signatures.filter(signature => signature.required && signature.enabled);
  }

  /**
   * Builds a concise tooltip for a signature icon.
   * @param signature Signature represented by the icon.
   * @returns Tooltip text describing the signature state.
   */
  signatureTooltip(signature: ScfHoldPointDashboardSignature): string {
    const signatureLabel = signature.requesterSignature ? 'Requester signature' : (signature.title || 'Signature');
    const assignees = signature.requesterSignature
      ? (signature.users?.[0] || signature.roles?.[0] || '')
      : [...(signature.roles || []), ...(signature.users || [])]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(', ');
    if (signature.signed) {
      return `${signatureLabel} — signed by ${signature.signedBy || 'user'}`;
    }
    return `${signatureLabel}${assignees ? ` (${assignees})` : ''} — not signed`;
  }

  /**
   * Supplies stable row identity to the matrix renderer.
   * @param _ Angular row index.
   * @param row Dashboard row being rendered.
   * @returns Stable SCF ID.
   */
  trackByRow(_: number, row: ScfHoldPointDashboardRow): number {
    return row.id;
  }

  /**
   * Supplies stable signature identity to the signature renderer.
   * @param _ Angular signature index.
   * @param signature Signature being rendered.
   * @returns Stable signature ID.
   */
  trackBySignature(_: number, signature: ScfHoldPointDashboardSignature): number {
    return signature.id;
  }

  /**
   * Formats the stored comma-separated location names like the original SCF list.
   * @param value Stored location names.
   * @returns Location names with readable comma spacing.
   */
  formatLocationNames(value: string | undefined): string {
    return (value || '').replace(/,/g, ', ');
  }

  /**
   * Filters by requester, location, serial number, or SCF description.
   * @param value Free-text filter value.
   */
  private applyFilter(value: string): void {
    const filter = this.normalize(value);
    this.filteredRows = !filter
      ? [...this.rows]
      : this.rows.filter(row => this.normalize([
        row.serialNo,
        row.requesterName,
        row.locationNames,
        this.stripHtml(row.description)
      ].join(' ')).includes(filter));
  }

  /**
   * Normalizes free-text filter values for case-insensitive matching.
   * @param value Filter text to normalize.
   * @returns Normalized filter text.
   */
  private normalize(value: string | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  /**
   * Removes markup before including descriptions in free-text filtering.
   * @param value Description that may contain HTML markup.
   * @returns Plain-text description.
   */
  stripHtml(value: string | undefined): string {
    return (value || '').replace(/<[^>]*>/g, ' ');
  }
}
